// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

// Game types matching backend models
// Using const objects instead of enums for compatibility with erasableSyntaxOnly

import type { CardContent } from './content';

export const GameStatusEnum = {
  LOBBY: 'lobby',
  DEALING: 'dealing',
  NEW_ROUND_SETUP: 'new_round_setup',
  JUDGE_ADDS_ANSWER_TO_PROMPT: 'judge_adds_answer_to_prompt',
  PLAYERS_SUBMIT_CARDS: 'players_submit_cards',
  JUDGE_PICKS_WINNER: 'judge_picks_winner',
  ROUND_WRAP_UP: 'round_wrap_up',
  GAME_OVER: 'game_over',
} as const;
export type GameStatusEnum = (typeof GameStatusEnum)[keyof typeof GameStatusEnum];

export const PlayerActionStatusEnum = {
  IDLE: 'idle',
  EXPECTED_TO_ACT: 'expected_to_act',
  ACTION_TAKEN: 'action_taken',
  CONSIDERING_SWAP: 'considering_swap',
} as const;
export type PlayerActionStatusEnum =
  (typeof PlayerActionStatusEnum)[keyof typeof PlayerActionStatusEnum];

export const ClientActionEnum = {
  CLIENT_READY: 'CLIENT_READY',
  START_GAME: 'START_GAME',
  SELECT_CHARACTER: 'SELECT_CHARACTER',
  JUDGE_SUBMIT_ANSWER: 'JUDGE_SUBMIT_ANSWER',
  PLAYER_SUBMIT_CARDS: 'PLAYER_SUBMIT_CARDS',
  JUDGE_PICK_WINNER: 'JUDGE_PICK_WINNER',
  PLAYER_SWAP_CARDS: 'PLAYER_SWAP_CARDS',
  // Heartbeat
  PING: 'PING',
  // Chat actions
  CHAT_SEND_MESSAGE: 'CHAT_SEND_MESSAGE',
  CHAT_TYPING_START: 'CHAT_TYPING_START',
  CHAT_TYPING_STOP: 'CHAT_TYPING_STOP',
  CHAT_ADD_REACTION: 'CHAT_ADD_REACTION',
  CHAT_REMOVE_REACTION: 'CHAT_REMOVE_REACTION',
  CHAT_GET_HISTORY: 'CHAT_GET_HISTORY',
  // Lobby settings actions
  UPDATE_LOBBY_SETTINGS: 'UPDATE_LOBBY_SETTINGS',
  TOGGLE_SPECTATORS: 'TOGGLE_SPECTATORS',
  // Spectator actions
  JOIN_AS_SPECTATOR: 'JOIN_AS_SPECTATOR',
  LEAVE_AS_SPECTATOR: 'LEAVE_AS_SPECTATOR',
  // Profile actions
  UPDATE_PLAYER_PROFILE: 'UPDATE_PLAYER_PROFILE',
  // Terrible Psychiatrist game actions
  ADVANCE_TO_NEXT_ROUND: 'ADVANCE_TO_NEXT_ROUND',
  // Sick Day game actions (uses generic names)
  DISPLAY_PROMPT: 'DISPLAY_PROMPT',
  SUBMIT_EXCUSE: 'SUBMIT_EXCUSE',
  SUBMIT_VOTE: 'SUBMIT_VOTE',
  NEXT_ROUND: 'NEXT_ROUND',
  USE_POWER_UP: 'USE_POWER_UP',
  END_GAME: 'END_GAME',
} as const;
export type ClientActionEnum = (typeof ClientActionEnum)[keyof typeof ClientActionEnum];

export const ServerActionEnum = {
  PLAYER_JOINED_WS: 'PLAYER_JOINED_WS',
  PLAYER_LEFT_WS: 'PLAYER_LEFT_WS',
  GAME_STATE_UPDATE: 'GAME_STATE_UPDATE',
  GAME_STATE_DELTA: 'GAME_STATE_DELTA',
  ERROR_MESSAGE: 'ERROR_MESSAGE',
  HOST_REASSIGNED: 'HOST_REASSIGNED',
  JUDGE_FORFEITED: 'JUDGE_FORFEITED',
  ROOM_CLOSING: 'ROOM_CLOSING',
  // Heartbeat
  PONG: 'PONG',
  // Timer actions
  TIMER_START: 'TIMER_START',
  TIMER_TICK: 'TIMER_TICK',
  TIMER_WARNING: 'TIMER_WARNING',
  TIMER_COMPLETE: 'TIMER_COMPLETE',
  TIMER_PAUSED: 'TIMER_PAUSED',
  TIMER_RESUMED: 'TIMER_RESUMED',
  TIMER_CANCELLED: 'TIMER_CANCELLED',
  // Chat actions
  CHAT_NEW_MESSAGE: 'CHAT_NEW_MESSAGE',
  CHAT_TYPING_UPDATE: 'CHAT_TYPING_UPDATE',
  CHAT_REACTION_ADDED: 'CHAT_REACTION_ADDED',
  CHAT_REACTION_REMOVED: 'CHAT_REACTION_REMOVED',
  CHAT_HISTORY_RESPONSE: 'CHAT_HISTORY_RESPONSE',
  // Lobby settings and spectator actions
  LOBBY_SETTINGS_UPDATED: 'LOBBY_SETTINGS_UPDATED',
  SPECTATOR_JOINED: 'SPECTATOR_JOINED',
  SPECTATOR_LEFT: 'SPECTATOR_LEFT',
  SPECTATOR_COUNT_UPDATE: 'SPECTATOR_COUNT_UPDATE',
  SPECTATOR_KICKED: 'SPECTATOR_KICKED',
  // Profile updates
  PLAYER_PROFILE_UPDATED: 'PLAYER_PROFILE_UPDATED',
  // NPC Claiming
  PLAYER_CLAIMED: 'PLAYER_CLAIMED', // NPC was claimed by a human
  PLAYER_CONVERTED_TO_NPC: 'PLAYER_CONVERTED_TO_NPC', // Human disconnected, now NPC
} as const;
export type ServerActionEnum = (typeof ServerActionEnum)[keyof typeof ServerActionEnum];

export interface AssetContent {
  content_type: 'image' | 'video' | 'audio' | 'text';
  content_id: string;
  access_uris: string[];
  alt_text?: string | null;
}

export interface Asset {
  id: string;
  name?: string | null;
  description?: string | null;
  content: AssetContent[];
}

export interface Card {
  id: string;
  text: string;
  asset?: Asset | null;
  card_type: string;
  image_url?: string | null;
  // New: Rich content support
  contents?: CardContent[];
}

export interface QuestionCard extends Card {
  card_type: 'QuestionCard';
}

export interface AnswerCard extends Card {
  card_type: 'AnswerCard';
}

export interface TreatmentCard extends Card {
  card_type: 'TreatmentCard';
}

export interface PlayerInfo {
  player_id: string;
  display_name: string;
  score: number;
  is_judge: boolean;
  is_connected: boolean;
  is_host?: boolean;
  is_guest?: boolean;
  is_npc?: boolean; // Whether this player is an NPC
  claim_code?: string | null; // Claim code for NPC (only visible to host)
  profile_emoji?: string | null;
  theme_primary_color?: string | null;
}

export interface WinningTreatmentInfo {
  player_id: string;
  submitted_cards: (QuestionCard | AnswerCard | TreatmentCard)[];
}

export interface GameSettings {
  score_to_win: number;
  hand_size: number;
}

export interface GameConfiguration {
  game_id: string;
  description: string;
  num_cards_for_prompt: number;
  num_therapist_selected_cards_for_prompt: number;
  num_cards_for_response_min: number;
  num_cards_for_response_max: number;
  allow_card_swapping: boolean;
}

export interface GameState {
  room_status: GameStatusEnum;
  game_id: string; // game ID (e.g., "debate_club")
  current_round: number;
  current_judge: PlayerInfo | null;
  your_hand: TreatmentCard[];
  your_answer_hand: AnswerCard[];
  your_player_id: string;
  is_judge: boolean;
  all_players: PlayerInfo[];
  prompt_elements: (QuestionCard | AnswerCard)[];
  judge_answer_options: AnswerCard[];
  judge_question_options: QuestionCard[];
  player_submissions: Record<string, TreatmentCard[] | 'submitted'>;
  player_action_statuses: Record<string, PlayerActionStatusEnum>;
  winning_submission_info: WinningTreatmentInfo | null;
  expected_action: string | null;
  active_player_id: string | null;
  game_config: GameConfiguration;
}

export interface SpectatorInfo {
  player_id: string;
  display_name: string;
  joined_at: string;
  is_guest: boolean;
}

export interface LobbySettings {
  max_players: number;
  min_players: number;
  score_to_win: number;
  hand_size?: number;
  max_rounds?: number;
  enabled_packs?: number[] | null; // Pack selection (null = all packs)
  [key: string]: number | boolean | string | number[] | null | undefined; // Game-specific settings
}

export interface GamePreset {
  name: string;
  display_name: string;
  description: string;
  duration_estimate: string;
  settings: LobbySettings;
}

export interface GameConfigSchema {
  [settingName: string]: {
    type: 'number' | 'integer' | 'float' | 'boolean' | 'string' | 'multi_select';
    min?: number;
    max?: number;
    allowed_values?: string[] | null;
    default: number | boolean | string | number[] | null;
    description?: string;
    pack_metadata_endpoint?: string;
    min_selections?: number;
    max_selections?: number;
  };
}

export interface LobbyState {
  room_status: 'lobby';
  game_id: string; // game ID
  current_players: PlayerInfo[];
  your_player_id: string;
  is_host: boolean;
  player_action_statuses: Record<string, PlayerActionStatusEnum>;
  is_public?: boolean; // Room privacy setting
  min_players?: number; // Minimum players required to start (from game config)
  max_players?: number; // Maximum players allowed (from game config)
  spectators?: SpectatorInfo[]; // Spectators watching the game
  lobby_settings?: LobbySettings; // Dynamic game settings
  allow_spectators?: boolean; // Whether spectators are allowed
}

export interface WebSocketMessage {
  action: ServerActionEnum | ClientActionEnum;
  payload: Record<string, unknown>;
}

export interface RoomInfo {
  session_id: string;
  room_code: string;
  game_id: string;
  status: GameStatusEnum;
  num_players: number;
  max_players: number;
  host_name: string;
  current_round: number;
  is_full: boolean;
  is_started: boolean;
  is_public: boolean;
  player_ids: string[]; // List of player IDs for rejoin detection
  allow_spectators?: boolean; // Whether spectators are allowed for this session
}

export interface RoomListResponse {
  rooms: RoomInfo[];
  total: number;
}

export interface CardsListResponse {
  questions: QuestionCard[];
  answers: AnswerCard[];
  treatments: TreatmentCard[];
  totals: {
    questions: number;
    answers: number;
    treatments: number;
    total: number;
  };
}

export interface CreateRoomRequest {
  host_display_name: string;
  game_id?: string; // Optional game ID (defaults to server default)
}

export interface CreateRoomResponse {
  session_id: string;
  room_code: string;
  player_id: string;
  player_token: string;
  game_id: string; // The game for this room
  players: PlayerInfo[];
}

// games API types
export interface GameSummary {
  game_id: string;
  display_name: string;
  description: string;
  version: string;
  min_players: number;
  max_players: number;
  is_enabled: boolean;
  tags: string[];
  difficulty?: 'easy' | 'normal' | 'hard';
  category?: 'party' | 'strategy' | 'creative' | 'social';
  preview_url?: string;
  how_to_play?: string[];
  player_count_active?: number;
}

export interface GamesResponse {
  modes: GameSummary[];
  total_modes: number;
  plugin_system_enabled: boolean;
}

export interface JoinRoomRequest {
  session_id: string;
  display_name: string;
}

export interface JoinRoomResponse {
  session_id: string;
  room_code: string;
  player_id: string;
  player_token: string;
  current_players: PlayerInfo[];
}

export interface RoomLookupResponse {
  session_id: string;
  room_code: string;
}

// Admin types
export interface AdminStatsResponse {
  sessions: {
    total: number;
    with_active_connections: number;
  };
  connections: {
    total: number;
    by_session: Record<string, number>;
  };
  cards: {
    questions: number;
    answers: number;
    treatments: number;
    total: number;
  };
  redis_healthy: boolean;
  latency?: {
    average: number; // Average latency in ms across all players
    p95: number; // 95th percentile latency
    max: number; // Maximum latency
  };
}

export interface AdminSessionPlayer {
  player_id: string;
  display_name: string;
  score: number;
  is_connected: boolean;
  current_latency?: number; // Latest RTT in ms
}

export interface AdminSessionInfo {
  session_id: string;
  status: string;
  game_id: string;
  current_round: number;
  host: {
    player_id: string;
    display_name: string;
  };
  players: AdminSessionPlayer[];
  total_players: number;
  active_connections: number;
}

export interface AdminSessionsResponse {
  sessions: AdminSessionInfo[];
  total: number;
}

export interface AdminSessionDetailResponse {
  session_id: string;
  status: string;
  game_id: string;
  current_round_number: number;
  current_judge_idx: number | null;
  player_order: string[];
  players: Record<
    string,
    {
      display_name: string;
      score: number;
      is_connected: boolean;
      hand_size: number;
      current_latency?: number; // Latest RTT in ms
    }
  >;
  current_comic: {
    prompt_elements: Card[];
    judge_question_options_count: number;
    judge_answer_options_count: number;
    submissions_count: number;
    winning_treatment_info: WinningTreatmentInfo | null;
  };
  player_action_statuses: Record<string, string>;
  settings: Record<string, unknown>;
  active_websocket_connections: string[];
}

export interface AdminDeleteResponse {
  status: string;
  session_id: string;
  message: string;
}

export interface AdminFlushResponse {
  status: string;
  sessions_deleted: number;
  message: string;
}

export interface AdminBroadcastResponse {
  status: string;
  session_id: string;
  delivered_to: number;
  message: string;
}

export interface AdminUserInfo {
  user_id: string;
  username: string;
  permissions: string[];
  created_at: string;
  last_login: string | null;
  last_activity: string | null;
  is_disabled: boolean;
  disabled_at: string | null;
  profile_emoji: string | null;
  theme_primary_color: string | null;
}

export interface AdminUsersResponse {
  users: AdminUserInfo[];
  total: number;
}

export interface AdminUserUpdateRequest {
  username?: string;
  permissions?: string[];
}

export interface AdminUserAuditEvent {
  event_type: string;
  timestamp: string;
  details: Record<string, unknown>;
}

export interface AdminUserAuditHistoryResponse {
  events: AdminUserAuditEvent[];
  total: number;
  has_more: boolean;
}

export interface AdminPasswordResetResponse {
  temporary_password: string;
  message: string;
}

export interface AdminUserDeleteResponse {
  status: string;
  user_id: string;
  username: string;
  message: string;
}

// Active Session (for single-game-per-user enforcement)
export interface ActiveSession {
  session_id: string;
  room_code: string;
  game_id: string;
  player_id: string;
  joined_at: number;
  phase?: string; // "lobby" or game-specific phase
  player_token?: string; // included when includeToken=true is passed to getMySession
}

export interface MySessionResponse {
  active_session: ActiveSession | null;
}

export interface LeaveSessionResponse {
  status: 'ok' | 'no_session';
  message: string;
  left_session_id?: string;
}

// NPC Claim types
export interface ClaimNpcRequest {
  claim_code: string;
}

export interface ClaimNpcResponse {
  status: string;
  session_id: string;
  room_code: string;
  player_id: string;
  player_token: string;
  display_name: string;
  score: number;
  message: string;
}

// Session conflict error response (HTTP 409)
export interface SessionConflictError {
  code: 'SESSION_CONFLICT';
  message: string;
  existing_session: ActiveSession;
}

// Type guards for discriminating GameState vs LobbyState
export function isLobbyState(state: GameState | LobbyState | null): state is LobbyState {
  return state !== null && state.room_status === 'lobby';
}

export function isActiveGameState(state: GameState | LobbyState | null): state is GameState {
  return state !== null && state.room_status !== 'lobby';
}

// Helper to safely get players from either state type
export function getPlayersFromState(state: GameState | LobbyState | null): PlayerInfo[] {
  if (!state) return [];
  if (isLobbyState(state)) {
    return state.current_players || [];
  }
  return state.all_players || [];
}

// Admin Invite Code types
export interface AdminInviteCodeInfo {
  invite_id: string;
  code: string;
  max_uses: number;
  current_uses: number;
  remaining_uses: number | null;
  created_by: string;
  created_at: string;
  expires_at: string | null;
  is_revoked: boolean;
  is_expired: boolean;
  is_active: boolean;
  note: string | null;
}

export interface AdminInviteListResponse {
  invites: AdminInviteCodeInfo[];
  total: number;
}

export interface AdminInviteUsage {
  user_id: string;
  username: string;
  used_at: string;
}

export interface AdminInviteUsageResponse {
  invite_id: string;
  code: string;
  usages: AdminInviteUsage[];
  total_uses: number;
}

export interface AdminCreateInviteRequest {
  max_uses?: number;
  expires_in_days?: number;
  note?: string;
}

export interface AdminCreateInviteResponse {
  invite_id: string;
  code: string;
  max_uses: number;
  expires_at: string | null;
  note: string | null;
}

export interface AdminRevokeInviteResponse {
  status: string;
  message: string;
}

// Test Mode types
export interface TestmodeStatusResponse {
  enabled: boolean;
  config: {
    default_ttl_seconds: number;
    max_players_per_group: number;
    max_spectators_per_group: number;
  };
}

export interface ModeSettingsResponse {
  testmode_enabled: boolean;
  devmode_enabled: boolean;
  auto_login_enabled: boolean;
}

export interface ModeToggleResponse {
  mode: string;
  previous: boolean;
  current: boolean;
  message: string;
}

// Session Debug types (SessionInspector)
export interface ComponentInfo {
  component_id: string;
  component_type: string;
  data: Record<string, unknown>;
  last_updated: string | null;
}

export interface PhaseInfo {
  current_phase: string;
  available_transitions: string[];
  phase_history: string[];
  has_phase_machine: boolean;
}

export interface PlayerDebugData {
  player_id: string;
  display_name: string;
  score: number;
  is_connected: boolean;
  is_host: boolean;
  is_npc: boolean;
  persona_id: string | null;
  hand_size: number | null;
  custom_data: Record<string, unknown>;
}

export interface SessionDebugInfo {
  session_id: string;
  game_id: string;
  phase: string;
  player_count: number;
  connected_players: number;
  components: ComponentInfo[];
  phase_info: PhaseInfo;
  players: PlayerDebugData[];
  created_at: string | null;
  updated_at: string | null;
  current_round: number;
  max_rounds: number;
}

export interface SessionDebugResponse {
  debug_info: SessionDebugInfo;
}
